import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { ArrowDown2, CloseCircle, SearchNormal } from 'iconsax-react'
import {
  RiCheckboxBlankLine,
  RiCheckboxIndeterminateLine,
  RiCheckboxLine
} from 'react-icons/ri'
import { useTheme } from '@mui/material'

import Infinite from '@SonarCX/components/organisms/ChatWindow/Timeline/Infinite'
import Button from '@SonarCX/components/atoms/Button2'
import Search from '@SonarCX/components/molecules/Search'
import ButtonMenu from '@SonarCX/components/molecules/ButtonMenu2'
import Tab from '../Tab'
import Notification from '../Notification'
import LoadingSpinner from '@SonarCX/components/atoms/LoadingSpinner'

import { GroupOptions, TypeOptions, filterNotifications } from '../helpers'
import { arraysEqual, toTitleCase } from '@SonarCX/utilities'
import { selectDMs } from '@SonarCX/slices/dms'
import {
  PAGE_SIZE,
  selectNotification,
  loadAll as loadAllNotifications,
  markRead as markNotificationRead,
  markAllRead,
  getNextPage
} from '@SonarCX/slices/notification'
import useSavedValue from '@SonarCX/hooks/useSavedValue'
import useStyles from './styles'

type PageProps = {
  rootRef: React.RefObject<HTMLDivElement>
  hideTitle?: boolean
  className?: string
}

const Page: React.FC<PageProps> = ({ rootRef, hideTitle, className }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { dms } = useAppSelector(selectDMs)
  const { all, unread, loading, loaded } = useAppSelector(selectNotification)

  const [searching, setSearching] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [checked, setChecked] = React.useState([])
  const allChecked = React.useMemo(() => {
    return arraysEqual(checked, Object.keys(all))
  }, [checked, all])
  const [group, setGroup] = React.useState<GroupOptions>('all')
  const [type, setType] = useSavedValue<TypeOptions>({
    key: 'notifications-tab',
    defaultValue: '"unread"'
  })

  const notifs = React.useMemo(() => {
    const notifications = type === 'unread' ? unread : all
    return filterNotifications({
      notifications: Object.values(notifications),
      group,
      type,
      search
    })
  }, [unread, all, group, type, search])
  const allCheckedInList = React.useMemo(() => {
    return arraysEqual(
      checked,
      notifs.map(n => n.id)
    )
  }, [checked, notifs])
  const isFiltered = React.useMemo(
    () => group !== 'all' || type !== 'all' || search.trim().length > 0,
    [group, type, search]
  )
  const unreadCounts = React.useMemo(() => {
    const unreadCount = filterNotifications({
      notifications: Object.values(unread),
      group,
      type: 'unread',
      search
    }).length
    const mentionsCount = filterNotifications({
      notifications: Object.values(unread),
      group,
      type: 'mentions',
      search
    }).length
    return {
      all: 0,
      unread: unreadCount,
      mentions: mentionsCount,
      cleared: 0
    }
  }, [group, search, unread])

  const unreadNotifs = React.useMemo(() => Object.values(unread), [unread])
  const dmIDs = React.useRef([])

  const classes = useStyles()

  // Load all notifications when this page is first hit
  React.useEffect(() => {
    if (!loaded && !loading) {
      dispatch(loadAllNotifications())
    }
  }, [loaded, loading, dispatch])

  React.useEffect(() => {
    dmIDs.current = Object.keys(dms)
  }, [dms])

  const handleMarkAllAsReadClick = React.useCallback(() => {
    if (allChecked || checked.length === 0) {
      dispatch(markAllRead())
    } else {
      checked.map(id => dispatch(markNotificationRead(id)))
    }

    setChecked([])
  }, [allChecked, checked, dispatch])

  const handleGetNextPage = React.useCallback(async () => {
    const result = await new Promise(res => {
      dispatch(getNextPage(result => res(result)))
    })
    return result
  }, [dispatch])

  const clearChecked = React.useCallback(() => {
    setChecked([])
  }, [])

  const checkAll = React.useCallback(() => {
    setChecked(notifs.map(n => n.id))
  }, [notifs])

  const renderGroup = React.useCallback(group => {
    return group
      .split(' ')
      .map(word => toTitleCase(word))
      .join(' ')
  }, [])

  const searchComponent = React.useMemo(() => {
    const iconProps = {
      size: 24,
      style: {
        cursor: 'pointer',
        marginRight: hideTitle ? '0.5rem' : 0
      },
      color: '#404040'
    }

    if (!searching) {
      return <SearchNormal onClick={() => setSearching(true)} {...iconProps} />
    }

    return (
      <>
        <Search
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
          hideIcon
          placeholder='Search...'
          className={classes.search}
          style={{ marginRight: hideTitle ? '0.5rem' : '1rem' }}
        />

        <CloseCircle
          onClick={() => {
            setSearch('')
            setSearching(false)
          }}
          {...iconProps}
        />
      </>
    )
  }, [searching, search, classes.search, hideTitle])

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.header}>
        <div className={classes.headerBG} />

        <div className={clsx(classes.headerContent, 'headerContent')}>
          {!hideTitle && (
            <div className={classes.headerTitleArea}>
              <div className={classes.title}>Notifications</div>

              {searchComponent}
            </div>
          )}

          <div className={classes.controls}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {searchComponent}

              {allCheckedInList ? (
                <RiCheckboxLine
                  size={theme.size.base}
                  onClick={clearChecked}
                  className={classes.checkbox}
                />
              ) : checked.length > 0 ? (
                <RiCheckboxIndeterminateLine
                  size={theme.size.base}
                  onClick={checkAll}
                  className={classes.checkbox}
                />
              ) : (
                <RiCheckboxBlankLine
                  size={theme.size.base}
                  onClick={checkAll}
                  className={classes.checkbox}
                />
              )}

              <div className={classes.text}>Group</div>

              <ButtonMenu
                title='Group'
                options={['all', 'conversations', 'task']}
                onSelect={opt => setGroup(opt)}
                renderOption={opt => renderGroup(opt)}
                className={classes.menuRoot}
                menuProps={{
                  anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                  transformOrigin: { horizontal: 'center', vertical: 'top' }
                }}
              >
                {renderGroup(group)}
                <ArrowDown2 className={classes.arrowIcon} />
              </ButtonMenu>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className={classes.text} style={{ marginRight: '8px' }}>
                Type
              </div>

              <div className={classes.tabs}>
                {['unread', 'mentions', 'all', 'cleared'].map((title, i) => (
                  <Tab
                    key={`${title}-${i}`}
                    title={title}
                    selected={title === type}
                    onClick={() => setType(title as TypeOptions)}
                    count={unreadCounts[title]}
                  />
                ))}
              </div>
            </div>

            <Button
              disabled={unreadNotifs.length === 0}
              onClick={handleMarkAllAsReadClick}
              className={classes.markRead}
            >
              {checked.length === 0
                ? 'Mark all as read'
                : `Mark ${allCheckedInList ? 'all' : 'selected'} as read`}
            </Button>
          </div>
        </div>
      </div>

      {!loaded ? (
        <div className={classes.empty}>
          <LoadingSpinner />
        </div>
      ) : notifs.length === 0 ? (
        <div className={classes.empty}>
          You have no notifications
          {isFiltered ? ' matching these filters' : ''}
        </div>
      ) : (
        <Infinite
          containerRef={rootRef}
          getData={handleGetNextPage}
          PAGE_SIZE={PAGE_SIZE}
          loadAtTop={false}
          TRIGGER_HEIGHT={500}
          id='notifications'
        >
          {notifs.map((notification, i) => (
            <Notification
              key={`${notification.id}-${i}`}
              notification={notification}
              checked={checked.includes(notification.id)}
              toggleCheck={() =>
                setChecked(old => {
                  if (old.includes(notification.id)) {
                    return old.filter(id => id !== notification.id)
                  }
                  return [...old, notification.id]
                })
              }
              isLast={i === notifs.length - 1}
              onMarkRead={() => {
                setChecked(old => {
                  if (old.includes(notification.id)) {
                    return old.filter(id => id !== notification.id)
                  }
                  return old
                })
              }}
            />
          ))}
        </Infinite>
      )}
    </div>
  )
}

export default Page
